import { 
  LayoutGrid, 
  Gem, 
  Refrigerator, 
  Droplets, 
  Lightbulb, 
  Glasses, 
  Layers,
  PaintBucket,
  Check,
  X
} from "lucide-react";

const services = [
  {
    icon: LayoutGrid,
    title: "Cabinetry",
    description: "Kitchen & vanities with variety of door styles & colors.",
    sourcing: "Import & Domestic",
    scope: { materials: true, vacant: true, occupied: true }
  },
  {
    icon: Gem,
    title: "Countertops",
    description: "2 & 3 cm Granite and Quartz surfaces.",
    sourcing: "Import",
    scope: { materials: false, vacant: true, occupied: true }
  },
  {
    icon: Refrigerator,
    title: "Appliances",
    description: "Top brands including GE, LG, Samsung.",
    sourcing: "Domestic",
    scope: { materials: true, vacant: true, occupied: true }
  },
  {
    icon: Droplets,
    title: "Plumbing Fixtures",
    description: "Sinks, faucets, toilets, shower doors, heads, valves & drains.",
    sourcing: "Import & Domestic",
    scope: { materials: true, vacant: true, occupied: true }
  },
  {
    icon: Lightbulb,
    title: "Lighting Fixtures",
    description: "Lighting, ceiling fans, faceplates & switches.",
    sourcing: "Import & Domestic",
    scope: { materials: true, vacant: true, occupied: true }
  },
  {
    icon: Glasses,
    title: "Accessories",
    description: "Mirrors, blinds, bath kits & door hardware.",
    sourcing: "Import",
    scope: { materials: true, vacant: true, occupied: true }
  },
  {
    icon: Layers,
    title: "Flooring",
    description: "LVT materials with installation for vacant units.",
    sourcing: "Import & Domestic",
    scope: { materials: true, vacant: true, occupied: false }
  },
  {
    icon: PaintBucket,
    title: "Paint",
    description: "Complete interior painting services for vacant unit renovations.",
    sourcing: "N/A",
    scope: { materials: false, vacant: true, occupied: false }
  }
];

const ScopeIcon = ({ available }) => (
  available ? (
    <Check className="w-4 h-4 text-cyan-600" />
  ) : (
    <X className="w-4 h-4 text-slate-300" />
  )
);

const ServicesSection = () => {
  return (
    <section 
      id="services" 
      className="section-padding bg-white"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-cyan-600 font-semibold text-sm tracking-widest uppercase mb-4 block">
            Our Services
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-6">
            Complete Product{" "}
            <span className="text-cyan-600">Categories</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            From cabinetry to flooring, we provide everything needed for your 
            multifamily interior renovations—all sourced and installed by our team.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              className="service-card rounded-lg p-6 bg-white"
              data-testid={`service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="w-12 h-12 bg-cyan-100 rounded-md flex items-center justify-center mb-4">
                <service.icon className="w-6 h-6 text-cyan-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                {service.title}
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                {service.description}
              </p>
              
              {/* Sourcing Badge */}
              <div className="mb-4">
                <span className="text-xs font-medium text-slate-500">Sourcing: </span>
                <span className="text-xs font-semibold text-cyan-700 bg-cyan-50 px-2 py-0.5 rounded">
                  {service.sourcing}
                </span>
              </div>

              {/* Scope of Work */}
              <div className="border-t border-slate-100 pt-4">
                <p className="text-xs font-medium text-slate-500 mb-2">Scope of Work</p>
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1">
                    <ScopeIcon available={service.scope.materials} />
                    <span className="text-slate-600">Materials</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <ScopeIcon available={service.scope.vacant} />
                    <span className="text-slate-600">Vacant</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <ScopeIcon available={service.scope.occupied} />
                    <span className="text-slate-600">Occupied</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="mt-8 p-6 bg-slate-50 rounded-lg border border-slate-200">
          <h4 className="font-semibold text-slate-900 mb-4 text-sm">Scope of Work Legend</h4>
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-cyan-600">Materials Only</span>
              <p className="text-slate-600 text-xs mt-1">We supply, you install</p>
            </div>
            <div>
              <span className="font-medium text-cyan-600">Vacant Units</span>
              <p className="text-slate-600 text-xs mt-1">Full turnkey renovation</p>
            </div>
            <div>
              <span className="font-medium text-cyan-600">Occupied Turns</span>
              <p className="text-slate-600 text-xs mt-1">Efficient tenant-friendly approach</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
